/**
 * Personal Info Section - Modern profile information editor
 */
import * as React from 'react';
import { useState, useCallback, useEffect, useRef } from 'react';
import { observer } from 'mobx-react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { runInAction } from 'mobx';
import { Spinner } from 'reactstrap';
import { useRootStore } from '../../Store/useRootStore';
import { useToast } from '../../Hooks/useToast';
import { useTrans } from '../../Store/System/LocalizationStore';
import { Athlete, AthleteApi } from '../../Model/Athlete/Athlete';
import { GenderType } from '../../Model/Athlete/GenderType';
import { HttpBackend } from '../../Services/Http/HttpBackend';

function cloneAthlete(a: Athlete): Athlete {
  return { ...a, tags: [...a.tags] };
}
import { Theme } from '../../Theme/Theme';
import { Button } from '../../Components/Button';
import { ProfileHeader } from './ProfileHeader';

// Username: 3-20 chars, letters, numbers, underscores only
const NICKNAME_REGEX = /^[a-zA-Z0-9_]{3,20}$/;

const SectionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
`;

const CardTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: ${Theme.Colors.textColor};
  margin: 0 0 20px 0;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const CardIcon = styled.span`
  font-size: 20px;
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Label = styled.label`
  font-size: 14px;
  color: ${Theme.Colors.charcoal};
`;

const Input = styled.input`
  padding: 12px 14px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.2s;
  background: white;

  &:focus {
    outline: none;
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 3px ${Theme.Colors.primary}20;
  }

  &:disabled {
    background: ${Theme.Colors.lightestGrey};
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: 12px 14px;
  border: 1px solid ${Theme.Colors.lighterGrey};
  border-radius: 10px;
  font-size: 15px;
  transition: all 0.2s;
  background: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${Theme.Colors.primary};
    box-shadow: 0 0 0 3px ${Theme.Colors.primary}20;
  }

  &:disabled {
    background: ${Theme.Colors.lightestGrey};
    cursor: not-allowed;
  }
`;

const ButtonRow = styled.div`
  margin-top: 26px;
`;

const SuccessMessage = styled.div`
  background: #ecfdf5;
  color: #065f46;
  padding: 12px 16px;
  border-radius: 10px;
  font-size: 14px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const NicknameInputWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
`;

const NicknamePrefix = styled.span`
  position: absolute;
  left: 14px;
  color: ${Theme.Colors.primary};
  font-weight: 600;
  font-size: 15px;
  pointer-events: none;
`;

const NicknameInput = styled(Input)<{ $hasError?: boolean; $isValid?: boolean }>`
  padding-left: 28px;
  border-color: ${(props) =>
    props.$hasError ? '#ef4444' : props.$isValid ? '#10b981' : Theme.Colors.lighterGrey};

  &:focus {
    border-color: ${(props) => (props.$hasError ? '#ef4444' : props.$isValid ? '#10b981' : Theme.Colors.primary)};
    box-shadow: 0 0 0 3px ${(props) =>
      props.$hasError ? 'rgba(239, 68, 68, 0.2)' : props.$isValid ? 'rgba(16, 185, 129, 0.2)' : `${Theme.Colors.primary}20`};
  }
`;

const InputStatusIcon = styled.div`
  position: absolute;
  right: 12px;
  display: flex;
  align-items: center;
`;

const ValidationMessage = styled.span<{ $type: 'error' | 'success' | 'info' }>`
  font-size: 12px;
  margin-top: 4px;
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${(props) =>
    props.$type === 'error' ? '#ef4444' : props.$type === 'success' ? '#10b981' : Theme.Colors.charcoal};
`;

export const PersonalInfoSection: React.FC = observer(() => {
  const { authentication } = useRootStore();
  const toast = useToast();
  const { t } = useTrans();

  const [athlete, setAthlete] = useState(() => authentication.athlete ? cloneAthlete(authentication.athlete) : ({} as Athlete));
  const [processing, setProcessing] = useState(false);
  const [saved, setSaved] = useState(false);

  // Nickname validation state
  const [nickname, setNickname] = useState(authentication.athlete?.nickname ?? '');
  const [isCheckingNickname, setIsCheckingNickname] = useState(false);
  const [nicknameError, setNicknameError] = useState<string | null>(null);
  const [nicknameAvailable, setNicknameAvailable] = useState(false);
  const nicknameCheckTimeout = useRef<NodeJS.Timeout | null>(null);
  const previousNickname = useRef(authentication.athlete?.nickname ?? '');

  useEffect(() => {
    setAthlete(authentication.athlete ? cloneAthlete(authentication.athlete) : ({} as Athlete));
    setNickname(authentication.athlete?.nickname ?? '');
    previousNickname.current = authentication.athlete?.nickname ?? '';
  }, [authentication.athlete]);

  // Debounced nickname uniqueness check
  const checkNicknameUniqueness = useCallback(
    async (value: string) => {
      // Same as current - no need to check
      if (value === previousNickname.current) {
        setNicknameAvailable(false);
        setNicknameError(null);
        return;
      }

      // Format validation first
      if (!NICKNAME_REGEX.test(value)) {
        setNicknameError(
          t('profile.nickname.invalid', {
            defaultValue: 'Username must be 3-20 characters (letters, numbers, underscores)',
          }),
        );
        setNicknameAvailable(false);
        return;
      }

      setIsCheckingNickname(true);
      setNicknameError(null);
      setNicknameAvailable(false);

      try {
        await AthleteApi.checkNickname(value);
        setNicknameAvailable(true);
        setNicknameError(null);
      } catch (error) {
        setNicknameError(t('profile.nickname.taken', { defaultValue: 'This username is already taken' }));
        setNicknameAvailable(false);
      } finally {
        setIsCheckingNickname(false);
      }
    },
    [t],
  );

  const handleChangeNickname = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const value = e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '');
      setNickname(value);
      setNicknameAvailable(false);
      setNicknameError(null);

      // Clear previous timeout
      if (nicknameCheckTimeout.current) {
        clearTimeout(nicknameCheckTimeout.current);
      }

      // Debounce the check
      if (value.length >= 3) {
        nicknameCheckTimeout.current = setTimeout(() => {
          checkNicknameUniqueness(value);
        }, 500);
      } else if (value.length > 0) {
        setNicknameError(
          t('profile.nickname.tooShort', { defaultValue: 'Username must be at least 3 characters' }),
        );
      }
    },
    [checkNicknameUniqueness, t],
  );

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      if (!authentication.athlete) return;

      setProcessing(true);
      setSaved(false);

      // Validate nickname before submit
      if (nickname && nickname !== previousNickname.current && (nicknameError || isCheckingNickname)) {
        toast.showToast(t('profile.nickname.waitValidation', { defaultValue: 'Please wait for username validation' }), 'error');
        setProcessing(false);
        return;
      }

      try {
        const updatedAthleteData = {
          id: athlete.id,
          firstname: athlete.firstname,
          nickname: nickname || undefined,
          birthYear: athlete.birthYear,
          gender: athlete.gender,
        };

        await HttpBackend.post('/athlete', updatedAthleteData);

        runInAction(() => {
          if (authentication.athlete) {
            authentication.athlete.firstname = athlete.firstname;
            authentication.athlete.nickname = nickname || undefined;
            authentication.athlete.birthYear = athlete.birthYear;
            authentication.athlete.gender = athlete.gender;
          }
        });
        previousNickname.current = nickname;

        setSaved(true);
        toast.showToast(t('profile.saved', { defaultValue: 'Profile saved successfully' }), 'success');
        setTimeout(() => setSaved(false), 3000);
      } catch (error) {
        console.error('Error updating profile:', error);
        toast.showToast(
          t('accountScreen.details.errorMessage', { defaultValue: 'An error occurred saving profile details.' }),
          'error',
        );
      } finally {
        setProcessing(false);
      }
    },
    [athlete, authentication, t, toast, nickname, nicknameError, isCheckingNickname],
  );

  const handleChangeName = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAthlete({ ...athlete, firstname: e.target.value });
    },
    [athlete],
  );

  const handleChangeBirthYear = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setAthlete({ ...athlete, birthYear: e.target.value ? Number(e.target.value) : undefined });
    },
    [athlete],
  );

  const handleChangeGender = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      setAthlete({ ...athlete, gender: e.target.value as GenderType });
    },
    [athlete],
  );

  return (
    <SectionContent>
      <ProfileHeader />

      <Card>
        <CardTitle>
          {t('profile.personalInfo', { defaultValue: 'Personal Information' })}
        </CardTitle>

        <form onSubmit={handleSubmit}>
          <FormGrid>
            <FormGroup>
              <Label htmlFor="profileName">
                {t('accountScreen.details.nameLabel', { defaultValue: 'Display Name' })}
              </Label>
              <Input
                id="profileName"
                type="text"
                value={athlete.firstname ?? ''}
                onChange={handleChangeName}
                disabled={processing}
                placeholder={t('profile.namePlaceholder', { defaultValue: 'Enter your name' })}
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="profileNickname">
                {t('profile.nickname.label', { defaultValue: 'Username' })}
              </Label>
              <NicknameInputWrapper>
                <NicknamePrefix>@</NicknamePrefix>
                <NicknameInput
                  id="profileNickname"
                  type="text"
                  value={nickname}
                  onChange={handleChangeNickname}
                  disabled={processing}
                  placeholder={t('profile.nickname.placeholder', { defaultValue: 'your_username' })}
                  $hasError={!!nicknameError}
                  $isValid={nicknameAvailable}
                  maxLength={20}
                  autoComplete="off"
                />
                <InputStatusIcon>
                  {isCheckingNickname && <Spinner size="sm" color="secondary" />}
                  {!isCheckingNickname && nicknameAvailable && (
                    <i className="bi bi-check-circle-fill" style={{ color: '#10b981', fontSize: 18 }} />
                  )}
                  {!isCheckingNickname && nicknameError && (
                    <i className="bi bi-x-circle-fill" style={{ color: '#ef4444', fontSize: 18 }} />
                  )}
                </InputStatusIcon>
              </NicknameInputWrapper>
              {nicknameError && <ValidationMessage $type="error">{nicknameError}</ValidationMessage>}
              {nicknameAvailable && (
                <ValidationMessage $type="success">
                  {t('profile.nickname.available', { defaultValue: 'Username is available!' })}
                </ValidationMessage>
              )}
              {!nicknameError && !nicknameAvailable && nickname.length === 0 && (
                <ValidationMessage $type="info">
                  {t('profile.nickname.hint', { defaultValue: '3-20 characters, letters, numbers, underscores' })}
                </ValidationMessage>
              )}
            </FormGroup>

            <FormGroup>
              <Label htmlFor="profileBirthYear">
                {t('accountScreen.details.birthYearLabel', { defaultValue: 'Birth Year' })}
              </Label>
              <Input
                id="profileBirthYear"
                type="number"
                min="1900"
                max={new Date().getFullYear()}
                value={athlete.birthYear ?? ''}
                onChange={handleChangeBirthYear}
                disabled={processing}
                placeholder="1990"
              />
            </FormGroup>

            <FormGroup>
              <Label htmlFor="profileGender">
                {t('accountScreen.details.genderLabel', { defaultValue: 'Gender' })}
              </Label>
              <Select
                id="profileGender"
                value={athlete.gender}
                onChange={handleChangeGender}
                disabled={processing}
              >
                <option value="FEMALE">{t('accountScreen.details.gender.female', { defaultValue: 'Female' })}</option>
                <option value="MALE">{t('accountScreen.details.gender.male', { defaultValue: 'Male' })}</option>
                <option value="UNKNOWN">{t('accountScreen.details.gender.other', { defaultValue: 'Other' })}</option>
              </Select>
            </FormGroup>
          </FormGrid>

          <ButtonRow>
            <Button type="submit" color="primary" size="sm" disabled={processing}>
              {processing
                ? t('globals.saving', { defaultValue: 'Saving...' })
                : t('accountScreen.details.saveChangesButton', { defaultValue: 'Save Changes' })}
            </Button>
          </ButtonRow>
        </form>

        {saved && (
          <SuccessMessage>
            ✓ {t('profile.saved', { defaultValue: 'Profile saved successfully' })}
          </SuccessMessage>
        )}
      </Card>
    </SectionContent>
  );
});
